import { useEffect, useState } from "react";
import { Truck, MapPin, Clock, CalendarDays, Loader2, ExternalLink, UtensilsCrossed } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { FoodTruckDialog } from "@/components/FoodTruckDialog";
import { UpgradeNotice } from "@/components/UpgradeNotice";
import { useAuth } from "@/context/AuthContext";
import { menuLink } from "@/lib/publicUrl";

const fixYear = (d) => { const m = /^(\d{1,4})-(\d{2}-\d{2})$/.exec(d || ""); return m && +m[1] < 100 ? `${(+m[1] + 2000).toString().padStart(4, "0")}-${m[2]}` : (d || ""); };
const fmtDate = (d) => (d ? new Date(`${fixYear(d)}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Rome" }) : "");
const fmtTime = (s) => (s.time_from ? `${s.time_from}${s.time_to ? `–${s.time_to}` : ""}` : "");
const todayIT = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const nowIT = () => new Intl.DateTimeFormat("it-IT", { timeZone: "Europe/Rome", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date());
const isPast = (s) => { const d = fixYear(s.date); return !!d && (d < todayIT() || (d === todayIT() && !!s.time_to && s.time_to < nowIT())); };

export default function FoodTruck() {
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [menus, setMenus] = useState([]);
  const canMenu = !!user?.subscription?.features?.foodtruck_menu;
  const menuName = (id) => menus.find((m) => m.id === id)?.name;

  useEffect(() => {
    api.get("/restaurant").then((r) => setRestaurant(r.data)).catch((e) => { logError("Caricamento FoodTruck fallito:", e); toast.error("Impossibile caricare i dati."); });
    api.get("/menus").then((r) => setMenus(r.data)).catch(() => {});
  }, []);

  if (!user?.is_paid) return (
    <div className="space-y-6" data-testid="foodtruck-page">
      <h1 className="font-display font-bold text-4xl flex items-center gap-3"><Truck className="h-8 w-8 text-[#5aa469]" /> FoodTruck</h1>
      <UpgradeNotice testid="foodtruck-upgrade" text="La funzione FoodTruck (luogo e orario nel menù pubblico) è disponibile dal piano Basic in su." />
    </div>
  );

  if (!restaurant) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5aa469]" /></div>;

  const ft = restaurant.foodtruck || {};
  const stops = [...(ft.stops || [])].sort((a, b) => (a.date + a.time_from).localeCompare(b.date + b.time_from));
  const single = !stops.length && ft.place ? [{ place: ft.place, date: ft.date, time_from: ft.time_from, time_to: ft.time_to, note: ft.note, menu_id: ft.menu_id }] : [];
  const list = stops.length ? stops : single;
  const menuUrl = menuLink(restaurant.slug);

  return (
    <div className="space-y-8" data-testid="foodtruck-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-4xl flex items-center gap-3"><Truck className="h-8 w-8 text-[#5aa469]" /> FoodTruck</h1>
          <p className="text-gray-500 mt-1">Comunica ai clienti dove e quando ti trovano: appare in cima al tuo menù pubblico.</p>
        </div>
        <FoodTruckDialog restaurant={restaurant} onSaved={setRestaurant} />
      </div>

      <div className={`rounded-2xl p-5 border ${ft.enabled ? "border-[#5aa469]/40 bg-[#5aa469]/8" : "border-gray-200 bg-gray-50"}`} data-testid="foodtruck-status">
        <p className="font-semibold">{ft.enabled ? "Visibile nel menù pubblico" : "Non visibile ai clienti"}</p>
        <p className="text-sm text-gray-500 mt-0.5">
          {ft.enabled ? "I clienti vedono il box “Oggi ci trovi qui” in cima al menù. Le tappe passate vengono nascoste automaticamente (ora italiana)." : "Attiva “Mostra nel menù pubblico” nel pulsante FoodTruck per pubblicarlo."}
        </p>
        {ft.enabled && list.length > 0 && list.every(isPast) && <p className="text-sm text-amber-700 mt-2 font-medium" data-testid="foodtruck-all-past">Tutte le tappe sono passate: il box non è più visibile ai clienti. Aggiungi una nuova tappa.</p>}
      </div>

      <div className="rounded-2xl mo-card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-[#5aa469]" />
          <h3 className="font-display font-semibold text-2xl">{stops.length ? "Tappe in programma" : "Luogo e orario"}</h3>
        </div>
        {list.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-400 text-sm">Nessun luogo impostato. Premi il pulsante <strong>FoodTruck</strong> in alto per aggiungerlo.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {list.map((s, i) => (
              <li key={`${s.date}-${s.place}-${i}`} className={`px-6 py-4 flex flex-wrap items-center gap-x-6 gap-y-1 ${isPast(s) ? "opacity-50" : ""}`} data-testid={`foodtruck-row-${i}`}>
                <span className="inline-flex items-center gap-2 font-medium"><MapPin className="h-4 w-4 text-[#5aa469]" />{s.place}</span>
                {isPast(s) && <span className="text-xs rounded-full bg-gray-200 text-gray-600 px-2 py-0.5" data-testid={`foodtruck-past-${i}`}>Passata</span>}
                {!isPast(s) && fixYear(s.date) === todayIT() && <span className="text-xs rounded-full bg-[#5aa469] text-white px-2 py-0.5" data-testid={`foodtruck-today-${i}`}>Oggi</span>}
                {s.date && <span className="inline-flex items-center gap-2 text-sm text-gray-600 capitalize"><CalendarDays className="h-4 w-4 text-gray-400" />{fmtDate(s.date)}</span>}
                {fmtTime(s) && <span className="inline-flex items-center gap-2 text-sm text-gray-600"><Clock className="h-4 w-4 text-gray-400" />{fmtTime(s)}</span>}
                {canMenu && s.menu_id && menuName(s.menu_id) && (
                  <Link to={`/dashboard/menu?menu=${s.menu_id}`} className="inline-flex items-center gap-2 text-sm font-medium text-[#4a8a56] hover:underline" data-testid={`foodtruck-menu-${i}`}>
                    <UtensilsCrossed className="h-4 w-4" />Menù FoodTruck: {menuName(s.menu_id)} <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
                {s.note && <span className="text-sm text-gray-400 w-full">{s.note}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <a href={menuUrl} target="_blank" rel="noreferrer">
        <Button variant="outline" className="rounded-full border-gray-300 bg-white hover:bg-gray-50" data-testid="foodtruck-preview"><ExternalLink className="h-4 w-4 mr-2" />Vedi come lo vedono i clienti</Button>
      </a>
    </div>
  );
}
